import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';

export type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_OPTIONS = [
  { value: 'active' as const, label: 'Active' },
  { value: 'inactive' as const, label: 'Inactive' },
  { value: 'all' as const, label: 'All' },
];

/**
 * The row above a settings list (Admin_Panel_Wireframe.html, D4): search,
 * an optional Active / Inactive / All filter, a count, and the page's one
 * primary action ("+ Add branch") on the right — always enabled; validation
 * happens in the drawer it opens, not by greying the button out.
 */
export function SettingsToolbar({
  search, onSearch, placeholder = 'Search', status, onStatus, count, action,
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder?: string;
  status?: StatusFilter;
  onStatus?: (v: StatusFilter) => void;
  count?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-full sm:w-[260px]">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="pl-8 text-[13px]" />
      </div>
      {status && onStatus && (
        <SegmentedControl options={STATUS_OPTIONS} value={status} onChange={onStatus} className="w-[240px] shrink-0" />
      )}
      <span className="flex-1" />
      {count != null && <span className="text-[12px] text-muted-foreground">{count}</span>}
      {action}
    </div>
  );
}

/** Applies a StatusFilter to rows with an `active` flag. */
export function filterByStatus<T extends { active: boolean }>(rows: T[], status: StatusFilter): T[] {
  if (status === 'all') return rows;
  return rows.filter((r) => (status === 'active' ? r.active : !r.active));
}

/** Case-insensitive match of a query against several fields. */
export function matchesSearch(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f?.toLowerCase().includes(q));
}

/** An empty optional value in a table cell. */
export function Dash() {
  return <span className="text-muted-foreground">—</span>;
}
